import { useCurrencyContext } from '@contexts/currency-context';
import { LeagueEvent } from '@interfaces/APITravel';
import { TicketGroup } from '@interfaces/ticketevolution/tikets';
import { useBuildPackagesStore } from '@store/build-package-store';
import { applyMargin } from '@utils/currency';
import { formatCurrencyV2 } from '@utils/format-currency-v2';
import { buildTicketGa4Item } from '@v2/analytics/ecommerce';
import { trackGa4ItemListView, trackGa4ItemSelection } from '@v2/analytics/ga4';
import { Skeleton, Typography } from '@v2/ui';
import { cn } from '@v2/utils';
import pluralize from 'pluralize';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  defaultRangeExtractor,
  useVirtualizer,
  type Range,
} from '@tanstack/react-virtual';
import { useTicketsStore } from 'src/stores/tickets-store';
import { TicketFiltersDropdown } from './TicketFiltersDropdown';
import { isAccessibleListing, isObstructedView } from '../utils/ticket-ranking';
import {
  cancelScheduledTicketTaxQuotePrefetch,
  prefetchTicketTaxQuote,
  scheduleTicketTaxQuotePrefetch,
} from '../utils/ticket-tax-quotes';
import { useFilteredSortedTickets } from '../utils/use-filtered-tickets';
import { useTicketPriceOffset } from '../utils/use-ticket-price-offset';

type Props = {
  event?: LeagueEvent;
  list: TicketGroup[];
  loading: boolean;
  /** Show filters dropdown + result count above the list (desktop). Mobile uses Sort & Filter sheet. */
  showFilters?: boolean;
};

export function TicketList({
  event,
  list,
  loading,
  showFilters = true,
}: Props) {
  const ticketSelected = useTicketsStore((state) => state.ticketSelected);
  const ticketsQuantity = useTicketsStore((state) => state.ticketsQuantity);
  const { priceRate, currency } = useCurrencyContext();

  const priceOffset = useTicketPriceOffset(list);

  const { filtered, greatDealIds, resultCount } =
    useFilteredSortedTickets(list);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const virtualizer = useVirtualizer({
    count: loading ? 0 : filtered.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 120,
    initialRect: { width: 0, height: 800 },
    getItemKey: useCallback((index: number) => filtered[index].id, [filtered]),
    overscan: 3,
    // Keep the focused row and its neighbors mounted so Tab can reach every listing.
    rangeExtractor: useCallback(
      (range: Range) => {
        const indexes = defaultRangeExtractor(range);
        if (focusedIndex === null) return indexes;
        return [
          ...new Set([
            ...indexes,
            focusedIndex - 1,
            focusedIndex,
            focusedIndex + 1,
          ]),
        ]
          .filter((index) => index >= 0 && index < range.count)
          .sort((a, b) => a - b);
      },
      [focusedIndex]
    ),
  });

  useEffect(() => {
    setFocusedIndex(null);
    virtualizer.scrollToOffset(0);
  }, [filtered, virtualizer]);

  const trackedLists = useRef(new Set<string>());
  const ga4Items = useMemo(
    () =>
      filtered.map((ticket, index) =>
        buildTicketGa4Item({
          eventName: event?.name,
          groupId: ticket.id,
          index,
          price: Number(
            applyMargin(
              Math.max(0, ticket.retail_price - priceOffset),
              priceRate,
              currency
            )
          ),
          quantity: ticketsQuantity,
          section: ticket.section,
        })
      ),
    [currency, event?.name, filtered, priceOffset, priceRate, ticketsQuantity]
  );
  const trackingKey = useMemo(
    () =>
      ga4Items
        .map((item) => `${item.item_id}:${item.price}:${item.quantity}`)
        .join('|'),
    [ga4Items]
  );

  useEffect(() => {
    if (loading || !ga4Items.length || trackedLists.current.has(trackingKey))
      return;
    trackedLists.current.add(trackingKey);
    trackGa4ItemListView({
      itemListId: 'ticket_results',
      itemListName: 'Ticket results',
      items: ga4Items,
    });
  }, [ga4Items, loading, trackingKey]);

  return (
    <div className="flex flex-col lg:min-h-0 lg:flex-1">
      {showFilters && !loading && list.length > 0 && (
        <div className="flex items-center justify-between gap-3 bg-gray-50 px-4 py-3 lg:px-6">
          <Typography size="txs" className="font-medium text-gray-500">
            {resultCount} {pluralize('listing', resultCount)}
          </Typography>
          <TicketFiltersDropdown ticketsList={list} />
        </div>
      )}

      <div
        ref={scrollRef}
        role="region"
        aria-label="Ticket listings"
        tabIndex={0}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget))
            setFocusedIndex(null);
        }}
        className="h-[55svh] overflow-y-auto p-4 pt-0 lg:h-auto lg:min-h-0 lg:flex-1 lg:p-6 lg:pt-0"
      >
        <div className="space-y-2 lg:space-y-3">
          {loading &&
            [0, 1, 2, 3, 4, 5].map((item) => (
              <Skeleton key={item} className="h-[94px] w-full" />
            ))}

          {!loading && !filtered.length && (
            <Typography
              size="tlg"
              className="text-center font-medium text-gray-500"
            >
              Sorry, we couldn&apos;t find any seats that matched your search.
            </Typography>
          )}
          {!loading && filtered.length > 0 && (
            <div
              role="list"
              className="relative w-full"
              style={{
                height:
                  virtualizer.getVirtualItems().length > 0
                    ? virtualizer.getTotalSize()
                    : undefined,
              }}
            >
              {virtualizer.getVirtualItems().length > 0
                ? virtualizer.getVirtualItems().map((virtualRow) => {
                    const item = filtered[virtualRow.index];
                    return (
                      <div
                        key={virtualRow.key}
                        ref={virtualizer.measureElement}
                        data-index={virtualRow.index}
                        role="listitem"
                        aria-posinset={virtualRow.index + 1}
                        aria-setsize={filtered.length}
                        onFocus={() => {
                          setFocusedIndex(virtualRow.index);
                          virtualizer.scrollToIndex(virtualRow.index, {
                            align: 'auto',
                          });
                        }}
                        className="absolute left-0 top-0 w-full pb-2 lg:pb-3"
                        style={{
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        <TicketItem
                          {...item}
                          priceOffset={priceOffset}
                          isItemSelected={ticketSelected?.id === item.id}
                          isGreatDeal={greatDealIds.has(item.id)}
                          eventName={event?.name}
                          index={virtualRow.index}
                          currency={currency}
                          priceRate={priceRate}
                        />
                      </div>
                    );
                  })
                : filtered.map((item, index) => (
                    <div
                      key={item.id}
                      data-index={index}
                      role="listitem"
                      aria-posinset={index + 1}
                      aria-setsize={filtered.length}
                      className="w-full pb-2 lg:pb-3"
                    >
                      <TicketItem
                        {...item}
                        priceOffset={priceOffset}
                        isItemSelected={ticketSelected?.id === item.id}
                        isGreatDeal={greatDealIds.has(item.id)}
                        eventName={event?.name}
                        index={index}
                        currency={currency}
                        priceRate={priceRate}
                      />
                    </div>
                  ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type TicketItemProps = TicketGroup & {
  priceOffset: number;
  isItemSelected: boolean;
  isGreatDeal?: boolean;
  eventName?: string;
  index?: number;
  currency?: string;
  priceRate?: number;
};

const TicketItem = memo(function TicketItem(props: TicketItemProps) {
  const {
    signature,
    section,
    retail_price,
    available_quantity,
    row,
    priceOffset,
    isItemSelected,
    isGreatDeal,
    public_notes,
    eventName,
    index,
  } = props;
  const setTicketSelected = useTicketsStore((state) => state.setTicketSelected);
  const ticketsQuantity = useTicketsStore((state) => state.ticketsQuantity);
  const currencyContext = useCurrencyContext();
  const priceRate = props.priceRate ?? currencyContext.priceRate;
  const currency = props.currency ?? currencyContext.currency;

  const isBundle = useBuildPackagesStore((state) => state.isBundle);

  const finalPrice = Math.max(0, retail_price - priceOffset);
  const accessible = isAccessibleListing(props);
  const obstructed = isObstructedView(public_notes);
  const ticketGroupId = props.id;

  useEffect(() => {
    return () => {
      cancelScheduledTicketTaxQuotePrefetch(ticketGroupId, ticketsQuantity);
    };
  }, [ticketGroupId, ticketsQuantity]);

  const prefetchTaxQuote = () => {
    prefetchTicketTaxQuote(ticketGroupId, ticketsQuantity);
  };

  return (
    <div
      className={cn(
        `rounded-xl border-2 border-gray-200 bg-white p-3 md:p-5 ${isItemSelected ? 'border-2 border-primary-500' : ''}`
      )}
    >
      <button
        type="button"
        aria-pressed={isItemSelected}
        className="flex w-full items-center justify-between gap-x-3 text-start sm:gap-x-4"
        onMouseEnter={() =>
          scheduleTicketTaxQuotePrefetch(ticketGroupId, ticketsQuantity)
        }
        onMouseLeave={() =>
          cancelScheduledTicketTaxQuotePrefetch(ticketGroupId, ticketsQuantity)
        }
        onFocus={() =>
          scheduleTicketTaxQuotePrefetch(ticketGroupId, ticketsQuantity)
        }
        onBlur={() =>
          cancelScheduledTicketTaxQuotePrefetch(ticketGroupId, ticketsQuantity)
        }
        onPointerDown={prefetchTaxQuote}
        onClick={() => {
          if (isItemSelected) {
            setTicketSelected(null);
          } else {
            prefetchTaxQuote();
            trackGa4ItemSelection({
              itemListId: 'ticket_results',
              itemListName: 'Ticket results',
              item: buildTicketGa4Item({
                eventName,
                groupId: ticketGroupId,
                index,
                price: Number(applyMargin(finalPrice, priceRate, currency)),
                quantity: ticketsQuantity,
                section,
              }),
            });
            setTicketSelected(props);
          }
        }}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-y-1 text-start">
          <div className="flex flex-wrap items-center gap-1.5">
            {isGreatDeal && (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700">
                Best value
              </span>
            )}
            {accessible && (
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase text-blue-700">
                Accessible
              </span>
            )}
            {obstructed && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700">
                Limited view
              </span>
            )}
          </div>
          <h3
            className="truncate text-sm font-bold text-gray-800 lg:text-base"
            title={`Section ${section}, Row ${row}`}
          >
            Section {section}, Row {row}
          </h3>
          <span className="truncate text-xs font-medium text-gray-500 lg:text-sm">
            {available_quantity} {pluralize('ticket', available_quantity)}{' '}
            {signature && ` • ${signature}`}
          </span>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-y-0.5 text-right sm:gap-y-1">
          <p className="whitespace-nowrap text-base font-bold text-gray-800 sm:text-lg lg:text-xl">
            {isBundle ? '+ ' : ''}
            {formatCurrencyV2(
              finalPrice,
              priceRate,
              currency,
              false,
              false,
              true,
              true
            )}
          </p>
          <span className="whitespace-nowrap text-xs font-medium text-gray-500 lg:text-sm">
            {isBundle ? 'Per person' : 'Before taxes & fees'}
          </span>
        </div>
      </button>
    </div>
  );
});
